import {
  GetPlanSetsDocument,
  GetPlanSetsQuery,
  SavePlan_Input,
  SavePlanSet_Input,
  SavePlanSetFormMutation,
  useSavePlanMutation,
  useSavePlanSetFormMutation
} from 'generated/graphql';
import _ from 'lodash';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

export type IFormState = {
  planSetId: string;
  uploadCount: number;
  planCount: number;
  apiFileQueue: string[];
  isUploading: boolean;
  didUpload: boolean;
  projectId: string;

};

const initialState: IFormState = {
  planSetId: '',
  apiFileQueue: [],
  uploadCount: 0,
  planCount: 0,
  isUploading: false,
  projectId: '',
  didUpload: false
};

export function useFormState(planSetId: string, projectId: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      planSetId: {
        label: 'id',
        type: 'text'
      },
      apiFileQueue: {
        label: 'Name of Plan',
        type: 'text'
      },
      isUploading: {
        label: 'projectId',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (planSetId !== '' && formState.planSetId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'planSetId',
          value: planSetId
        }
      });
    }
  }, [
    dispatch,
    formState.planSetId,
    planSetId
  ]);

  useEffect(() => {
    if (projectId !== '' && formState.projectId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);



  useEffect(() => {
    if (formState.uploadCount > 0 && formState.uploadCount === formState.planCount && formState.isUploading) {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'isUploading',
          value: false
        }
      });
    }
    if (formState.uploadCount > 0 && formState.uploadCount > formState.planCount && !formState.isUploading) {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'isUploading',
          value: true
        }
      });
    }
  }, [
    dispatch,
    formState.apiFileQueue.length,
    formState.isUploading,
    formState.didUpload,
    formState.uploadCount,
    formState.planCount
  ]);



  const [fnSavePlan] = useSavePlan();

  useEffect(() => {
    const tempState = [...formState.apiFileQueue];
    if (tempState.length > 0) {

      const fileId: string = tempState.shift() ?? '';
      (async () => {
        await fnSavePlan({
          planSetId: formState.planSetId,
          projectId: formState.projectId,
          planVersion: {
            apiFileId: fileId
          }
        });
      })();
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          apiFileQueue: tempState,
          didUpload: true,
          planCount: formState.planCount + 1
        }
      });

    }
  }, [
    dispatch,
    fnSavePlan,
    formState,
    formState.apiFileQueue,
    formState.isUploading,
    formState.planSetId,
    formState.projectId
  ]);

  return React.useMemo(
    () => {
      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}



export function useSavePlanSet() {
  const [
    fnMutate,
    tuple
  ] = useSavePlanSetFormMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SavePlanSet_Input) => {
      const resp = await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetPlanSetsQuery, SavePlanSetFormMutation>(GetPlanSetsDocument, (prevState, { mutationResult }) => {
            if (!prevState.planSets) {
              return prevState;
            }

            if (!mutationResult.data?.savePlanSet) {
              return prevState;
            }

            return {
              ...prevState,
              planSets: _.compact([
                ...prevState.planSets,
                mutationResult.data.savePlanSet
              ])

            };
          })
        }
      });
      return resp.data?.savePlanSet?.id;
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export function useSavePlan() {
  const [
    fnMutate,
    tuple
  ] = useSavePlanMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SavePlan_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
